import './index.scss';

import React, {useEffect, useMemo, useRef, useState} from "react";
import PropTypes from 'prop-types';

import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faAngleDown, faBug, faCirclePlus, faPenToSquare, faPuzzlePiece, faRightFromBracket, faUserCog, faUsersGear} from "@fortawesome/free-solid-svg-icons";

import DefaultAvatar from "../../assets/default_avatar.png";

const Header = ({
	isEdit,
	setIsEdit,
	onLogout,
	onSwitchProfile,
	setShowAddLink,
	showAddLink,
	profile,
	onOpenProfileManager,
	onOpenAccountSettings,
	onOpenReportIssue,
	onOpenBrowserExtensionGuide,
	userPhotoUrl,
	userEmail = "",
	defaultAvatarUrl = DefaultAvatar
}) => {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const menuRef = useRef(null);

	const normalizeProfileName = (profileName) => {
		const normalized = String(profileName ?? "").trim().toUpperCase();
		return normalized || "ALL";
	};

	const profileName = normalizeProfileName(profile?.profileName);

	const avatarSrc = useMemo(() => (
		typeof userPhotoUrl === "string" && userPhotoUrl.trim() ? userPhotoUrl : defaultAvatarUrl
	), [userPhotoUrl, defaultAvatarUrl]);

	useEffect(() => {
		if (!isMenuOpen) return;
		const handleDocumentClick = (event) => {
			if (menuRef.current && !menuRef.current.contains(event.target)) {
				setIsMenuOpen(false);
			}
		};
		const handleEscape = (event) => {
			if (event.key === "Escape") {
				setIsMenuOpen(false);
			}
		};
		document.addEventListener("mousedown", handleDocumentClick);
		document.addEventListener("keydown", handleEscape);
		return () => {
			document.removeEventListener("mousedown", handleDocumentClick);
			document.removeEventListener("keydown", handleEscape);
		};
	}, [isMenuOpen]);

	const runMenuAction = (action) => {
		setIsMenuOpen(false);
		action();
	};

	return (
		<>
			<div className="left-btn-container">
				<button
					aria-label={showAddLink ? "close add link" : "add link"}
					onClick={() => setShowAddLink(!showAddLink)}
					className={`add-button ${showAddLink ? "active" : ""}`}
				>
					<FontAwesomeIcon icon={faCirclePlus}/>
				</button>
			</div>
			<div className="title-container">
				<button
					type="button"
					className="profile-title-button"
					onClick={() => onSwitchProfile()}
					aria-label={`Switch profile from ${profileName}`}
				>
					<span className="header-title">LinkSpace: {profileName}</span>
				</button>
			</div>
			<div className="right-btn-container">
				<div className="account-menu-container" ref={menuRef}>
					<button
						type="button"
						className={`account-button ${isMenuOpen ? "open" : ""}`}
						aria-label="Open account menu"
						aria-expanded={isMenuOpen}
						onClick={() => setIsMenuOpen((prev) => !prev)}
					>
						<img src={avatarSrc} alt="Account avatar"/>
						<FontAwesomeIcon icon={faAngleDown} className="menu-arrow"/>
					</button>

					{isMenuOpen && (
						<div className="account-menu" role="menu" aria-label="Account menu">
							<div className="account-menu-header">
								<img src={avatarSrc} alt="" className="account-menu-avatar" aria-hidden="true"/>
								{userEmail.trim() ? (
									<span className="account-menu-email" title={userEmail}>{userEmail}</span>
								) : null}
							</div>

							<div className="account-menu-body">
								<div className="account-menu-section" role="group" aria-label="Workspace">
									<span className="account-menu-section-label">Workspace</span>
									<button
										type="button"
										role="menuitem"
										className={isEdit ? "is-active" : ""}
										onClick={() => runMenuAction(() => setIsEdit((prev) => !prev))}
									>
										<span className="menu-item-icon" aria-hidden="true">
											<FontAwesomeIcon icon={faPenToSquare}/>
										</span>
										<span className="menu-item-text">
											<span className="menu-item-title">{isEdit ? "Leave edit mode" : "Edit links & categories"}</span>
											<span className="menu-item-hint">{isEdit ? "Return to browsing" : "Reorder and organize"}</span>
										</span>
									</button>
									<button type="button" role="menuitem" onClick={() => runMenuAction(() => onOpenProfileManager())}>
										<span className="menu-item-icon" aria-hidden="true">
											<FontAwesomeIcon icon={faUsersGear}/>
										</span>
										<span className="menu-item-text">
											<span className="menu-item-title">Manage profiles</span>
											<span className="menu-item-hint">Create and switch views</span>
										</span>
									</button>
								</div>

								<div className="account-menu-section" role="group" aria-label="Account">
									<span className="account-menu-section-label">Account</span>
									<button type="button" role="menuitem" onClick={() => runMenuAction(() => onOpenAccountSettings())}>
										<span className="menu-item-icon" aria-hidden="true">
											<FontAwesomeIcon icon={faUserCog}/>
										</span>
										<span className="menu-item-text">
											<span className="menu-item-title">Account settings</span>
											<span className="menu-item-hint">Email and preferences</span>
										</span>
									</button>
									<button type="button" role="menuitem" onClick={() => runMenuAction(() => onOpenBrowserExtensionGuide())}>
										<span className="menu-item-icon" aria-hidden="true">
											<FontAwesomeIcon icon={faPuzzlePiece}/>
										</span>
										<span className="menu-item-text">
											<span className="menu-item-title">Browser extension</span>
											<span className="menu-item-hint">Save links from any tab</span>
										</span>
									</button>
								</div>

								<div className="account-menu-section" role="group" aria-label="Support">
									<span className="account-menu-section-label">Support</span>
									<button type="button" role="menuitem" onClick={() => runMenuAction(() => onOpenReportIssue())}>
										<span className="menu-item-icon" aria-hidden="true">
											<FontAwesomeIcon icon={faBug}/>
										</span>
										<span className="menu-item-text">
											<span className="menu-item-title">Report a bug</span>
											<span className="menu-item-hint">Send feedback to the team</span>
										</span>
									</button>
								</div>
							</div>

							<div className="account-menu-footer">
								<button
									type="button"
									role="menuitem"
									className="sign-out-item"
									onClick={() => runMenuAction(() => onLogout())}
								>
									<span className="menu-item-icon" aria-hidden="true">
										<FontAwesomeIcon icon={faRightFromBracket}/>
									</span>
									<span className="menu-item-title">Sign out</span>
								</button>
							</div>
						</div>
					)}
				</div>
			</div>
		</>
	);
}

Header.propTypes = {
	isEdit: PropTypes.bool.isRequired,
	setIsEdit: PropTypes.func.isRequired,
	onLogout: PropTypes.func.isRequired,
	onSwitchProfile: PropTypes.func.isRequired,
	setShowAddLink: PropTypes.func.isRequired,
	showAddLink: PropTypes.bool.isRequired,
	profile: PropTypes.shape({
		id: PropTypes.string,
		profileName: PropTypes.string
	}),
	onOpenProfileManager: PropTypes.func.isRequired,
	onOpenAccountSettings: PropTypes.func.isRequired,
	onOpenReportIssue: PropTypes.func.isRequired,
	onOpenBrowserExtensionGuide: PropTypes.func.isRequired,
	userPhotoUrl: PropTypes.string,
	userEmail: PropTypes.string,
	defaultAvatarUrl: PropTypes.string,
};

export default Header;
